import React from "react"
import { Platform } from "react-native"

import { Audio, AVPlaybackStatus } from "expo-av"

import { AudioChunkT } from "./ChatContext"
import { useRiveContext } from "./RiveContext"
import { VisemeContext, VisemeContextType } from "./VisemeContext"

/**
 * Simplified VisemeProvider for unauthenticated screens (public content library).
 *
 * Differences from the full VisemeProvider:
 * - No MicrophoneContext dependency (no microphone in unauth)
 * - No ResponseTimingContext dependency
 * - No MSK / conversation callbacks
 * - Plays a single audio chunk at a time (no queue needed for intro message)
 * - Supports both base64 and audioUrl audio sources
 *
 * Provides the same VisemeContext object so AvatarOverlay's useVisemes() call resolves.
 */

export const UnauthVisemeProvider = ({ children }: React.PropsWithChildren) => {
  const { handleAnimateRive, stopRiveAnimation } = useRiveContext()

  const [isSpeaking, setIsSpeaking] = React.useState(false)
  const [isAudioBlocked, setIsAudioBlocked] = React.useState(false)
  const [isMuted, setIsMutedState] = React.useState(false)
  const [playbackRate, setPlaybackRate] = React.useState(1.0)

  const isMutedRef = React.useRef(false)
  const soundRef = React.useRef<Audio.Sound | null>(null)
  // Web: current HTMLAudioElement
  const webAudioRef = React.useRef<HTMLAudioElement | null>(null)
  // Resolvers waiting for speech to finish
  const speechQueueWaitersRef = React.useRef<Array<() => void>>([])
  // Tracks whether audio is pending replay after an autoplay block
  const isAudioBlockedRef = React.useRef(false)
  // Speech marks stored for lip-sync replay when deferred play fires on unmute
  const pendingSpeechMarksRef = React.useRef<AudioChunkT["speechMarks"] | null>(null)

  const resolveQueueWaiters = React.useCallback(() => {
    const waiters = speechQueueWaitersRef.current.splice(0)
    waiters.forEach((resolve) => resolve())
  }, [])

  const setMuted = React.useCallback(
    (muted: boolean) => {
      isMutedRef.current = muted
      setIsMutedState(muted)
      if (soundRef.current) {
        soundRef.current.setIsMutedAsync(muted).catch(() => {})
      }
      if (webAudioRef.current) {
        webAudioRef.current.muted = muted
      }
      // When the user unmutes while audio is pending (blocked by autoplay), attempt deferred playback.
      if (!muted && isAudioBlockedRef.current && webAudioRef.current) {
        const audio = webAudioRef.current
        isAudioBlockedRef.current = false
        setIsAudioBlocked(false)
        setIsSpeaking(true)
        handleAnimateRive?.(pendingSpeechMarksRef.current ?? [])
        audio.play().catch(() => {
          setIsSpeaking(false)
          webAudioRef.current = null
          resolveQueueWaiters()
        })
      }
    },
    [handleAnimateRive, resolveQueueWaiters],
  )

  const stopSpeech = React.useCallback(async () => {
    stopRiveAnimation?.()
    isAudioBlockedRef.current = false
    setIsAudioBlocked(false)
    pendingSpeechMarksRef.current = null
    if (soundRef.current) {
      try {
        await soundRef.current.stopAsync()
        await soundRef.current.unloadAsync()
      } catch {
        // Ignore errors during stop
      }
      soundRef.current = null
    }
    if (webAudioRef.current) {
      webAudioRef.current.pause()
      webAudioRef.current.src = ""
      webAudioRef.current = null
    }
    setIsSpeaking(false)
    resolveQueueWaiters()
  }, [stopRiveAnimation, resolveQueueWaiters])

  const queueSpeechNative = React.useCallback(
    async (chunk: AudioChunkT) => {
      await stopSpeech()

      const uri = chunk.audioUrl ?? `data:audio/mp3;base64,${chunk.base64}`
      if (!uri || uri === "data:audio/mp3;base64,undefined") return

      try {
        const { sound } = await Audio.Sound.createAsync(
          { uri },
          {
            shouldPlay: false,
            isMuted: isMutedRef.current,
            rate: playbackRate,
            shouldCorrectPitch: playbackRate > 1,
          },
        )
        soundRef.current = sound

        sound.setOnPlaybackStatusUpdate((status: AVPlaybackStatus) => {
          if (!status.isLoaded) return
          if (status.didJustFinish) {
            setIsSpeaking(false)
            sound.unloadAsync().catch(() => {})
            soundRef.current = null
            resolveQueueWaiters()
          }
        })

        setIsSpeaking(true)
        // Kick off Rive lip sync before play so animation starts in sync
        handleAnimateRive?.(chunk.speechMarks)
        await sound.playAsync()
      } catch (error) {
        console.warn("[UnauthVisemeProvider] Audio playback error:", error)
        setIsSpeaking(false)
        resolveQueueWaiters()
      }
    },
    [stopSpeech, playbackRate, handleAnimateRive, resolveQueueWaiters],
  )

  const queueSpeechWeb = React.useCallback(
    (chunk: AudioChunkT) => {
      stopSpeech().catch(() => {})

      const src = chunk.audioUrl ?? `data:audio/mp3;base64,${chunk.base64}`
      if (!src || src === "data:audio/mp3;base64,undefined") return

      const audio = new (window as any).Audio() as HTMLAudioElement
      audio.src = src
      audio.muted = isMutedRef.current
      audio.playbackRate = playbackRate
      audio.preload = "auto"
      webAudioRef.current = audio

      audio.onended = () => {
        setIsSpeaking(false)
        webAudioRef.current = null
        resolveQueueWaiters()
      }

      audio.onerror = () => {
        setIsSpeaking(false)
        webAudioRef.current = null
        resolveQueueWaiters()
      }

      pendingSpeechMarksRef.current = chunk.speechMarks

      setIsSpeaking(true)
      handleAnimateRive?.(chunk.speechMarks)

      audio.play().catch(() => {
        // Browser autoplay policy blocked playback (Chrome MEI, Safari, Firefox, etc.).
        // Stop the lip-sync animation since no audio is playing, but keep the audio element
        // alive in webAudioRef so setMuted(false) can replay it when the user unmutes.
        stopRiveAnimation?.()
        isAudioBlockedRef.current = true
        setIsAudioBlocked(true)
        setIsSpeaking(false)
        resolveQueueWaiters()
      })
    },
    [stopSpeech, stopRiveAnimation, playbackRate, handleAnimateRive, resolveQueueWaiters],
  )

  const queueSpeech = React.useCallback(
    (chunk: AudioChunkT) => {
      if (Platform.OS === "web") {
        queueSpeechWeb(chunk)
      } else {
        queueSpeechNative(chunk).catch(() => {})
      }
    },
    [queueSpeechNative, queueSpeechWeb],
  )

  const waitForSpeechQueueEmpty = React.useCallback((): Promise<void> => {
    if (!isSpeaking) return Promise.resolve()
    return new Promise<void>((resolve) => {
      speechQueueWaitersRef.current.push(resolve)
    })
  }, [isSpeaking])

  const value: VisemeContextType = React.useMemo(
    () => ({
      queueSpeech,
      stopSpeech,
      signalAllAudioReceived: () => {},
      isSpeaking,
      isAudioExpected: isSpeaking,
      isMuted,
      setMuted,
      setPlaybackRate,
      waitForSpeechQueueEmpty,
      isAudioBlocked,
    }),
    [
      queueSpeech,
      stopSpeech,
      isSpeaking,
      isMuted,
      setMuted,
      setPlaybackRate,
      waitForSpeechQueueEmpty,
      isAudioBlocked,
    ],
  )

  return <VisemeContext.Provider value={value}>{children}</VisemeContext.Provider>
}
